import React from "react";
import { YAxisProps, LineProps, ScatterProps } from "recharts";
import { getScaleConfig, getMetricColor } from './ChartScales';

/**
 * Analyzes the data structure to determine what metrics are available
 * and how they should be rendered on the chart, regardless of selection state
 */
export const getChartRenderingConfig = (
    data: any,
    getMetricInfo: (key: string) => any,
    mode: 'dark' | 'light' = 'dark'
) => {
    // Handle invalid data
    if (!data) {
        console.log("Chart rendering: data is null or undefined");
        return getEmptyConfig();
    }

    if (!data.datasets) {
        console.warn("Chart rendering: data.datasets is missing");
        return getEmptyConfig();
    }

    if (!Array.isArray(data.datasets)) {
        console.warn("Chart rendering: data.datasets is not an array");
        return getEmptyConfig();
    }

    // Extract all available metrics from the data
    const availableMetrics = data.datasets
        .filter((dataset: any) => {
            if (!dataset) {
                console.warn("Chart rendering: found null/undefined dataset");
                return false;
            }
            if (!dataset.id) {
                console.warn("Chart rendering: dataset missing metricKey", dataset);
                return false;
            }
            return true;
        }).map((dataset: any) => dataset.id);

    if (!availableMetrics.length) {
        console.warn("Chart rendering: no available metrics found");
        return getEmptyConfig();
    }

    // Process metrics and units
    try {
        // Fix: Explicitly cast the result to string[]
        const uniqueUnits: string[] = Array.from(
            new Set(availableMetrics.map((metric: string) => {
                try {
                    const info = getMetricInfo(metric);
                    return info.unit;
                } catch (err) {
                    console.error(`Error getting metric info for ${metric}:`, err);
                    return "unknown";
                }
            }))
        );

        // Ensure scaleGroups is a Map<string, string[]>
        const scaleGroups = new Map<string, string[]>();

        // Group metrics by their unit
        availableMetrics.forEach((metric: string) => {
            const unit = getMetricInfo(metric).unit;
            if (!scaleGroups.has(unit)) {
                scaleGroups.set(unit, []);
            }
            scaleGroups.get(unit)!.push(metric);
        });

        // Determine axes orientation based on scale config
        const leftAxes = uniqueUnits.filter(
            unit => getScaleConfig(unit, mode).orientation === 'left'
        );

        const rightAxes = uniqueUnits.filter(
            unit => getScaleConfig(unit, mode).orientation === 'right'
        );

        // Group metrics by their visualization type
        const metricsToRender = {
            regular: availableMetrics.filter(
                (metric: string) => !['boolean', 'status'].includes(getMetricInfo(metric).unit)
            ),
            boolean: availableMetrics.filter(
                (metric: string) => getMetricInfo(metric).unit === 'boolean'
            ),
            status: availableMetrics.filter(
                (metric: string) => getMetricInfo(metric).unit === 'status'
            )
        };

        // Helper to calculate domain with 5% margin
        const calculateDomainWithMargin = (unit: string): [number, number] => {
            const metricsInUnit = scaleGroups.get(unit) || [];
            let min = Infinity;
            let max = -Infinity;
            
            metricsInUnit.forEach(metric => {
                const dataset = data.datasets.find((ds: any) => ds.id === metric);
                if (dataset?.data) {
                    dataset.data.forEach((point: any) => {
                        const val = point.value;
                        if (typeof val === 'number' && !isNaN(val)) {
                            min = Math.min(min, val);
                            max = Math.max(max, val);
                        }
                    });
                }
            });
            
            if (min === Infinity || max === -Infinity) {
                return getScaleConfig(unit, mode).domain;
            }
            
            const range = max - min;
            const margin = range * 0.05;
            return [min - margin, max + margin];
        };

        // Create Y-axes configuration
        const yAxesConfig: YAxisProps[] = [
            // Left Y-axes
            ...leftAxes.map(unit => {
                const config = getScaleConfig(unit, mode);
                return {
                    key: `left-${unit}`,
                    yAxisId: config.yAxisId,
                    orientation: 'left',
                    domain: calculateDomainWithMargin(unit),
                    tickFormatter: (value: number) => Math.round(value).toString(),
                    label: {
                        value: config.yAxisId,
                        angle: -90,
                        position: 'insideLeft',
                        offset: 8,
                        style: { textAnchor: 'middle', fill: config.color, fontWeight: 'bold' }
                    },
                    tick: { fill: config.color, fontSize: 12, fontWeight: 'bold' },
                    axisLine: { stroke: config.color, strokeWidth: 2 },
                    tickLine: { stroke: config.color },
                    width: 55
                } as YAxisProps;
            }),

            // Right Y-axes
            ...rightAxes.map(unit => {
                const config = getScaleConfig(unit, mode);
                return {
                    key: `right-${unit}`,
                    yAxisId: config.yAxisId,
                    orientation: 'right',
                    domain: calculateDomainWithMargin(unit),
                    tickFormatter: (value: number) => Math.round(value).toString(),
                    label: {
                        value: config.unit,
                        angle: 90,
                        position: 'insideRight',
                        offset: 15,
                        style: { textAnchor: 'middle', fill: config.color, fontWeight: 'bold' }
                    },
                    tick: { fill: config.color, fontSize: 12, fontWeight: 'bold' },
                    axisLine: { stroke: config.color, strokeWidth: 2 },
                    tickLine: { stroke: config.color },
                    width: 50
                } as YAxisProps;
            })
        ];

        // Create line and scatter configurations
        const chartElementsConfig = {
            lines: [] as LineProps[],
            scatters: [] as ScatterProps[]
        };

        availableMetrics.forEach((metric: any) => {
            const metricInfo = getMetricInfo(metric);
            const dataPointCount = countDataPointsForMetric(data, metric);
            const isSparseData = dataPointCount < 20; // Threshold for sparse data

            // Render everything as lines, but configure differently for sparse data
            chartElementsConfig.lines.push({
                key: metric,
                type: "monotone",
                dataKey: metric,
                stroke: getMetricColor(metric, availableMetrics, getMetricInfo, mode),
                strokeWidth: isSparseData ? 1 : 2, // Make sparse lines slightly thinner
                name: metricInfo.label,
                yAxisId: getScaleConfig(metricInfo.unit, mode).yAxisId,
                connectNulls: isSparseData, // Connect nulls for sparse data
                dot: isSparseData ? { r: 4, fill: getMetricColor(metric, availableMetrics, getMetricInfo, mode) } : false, // Show dots for sparse data
                activeDot: { r: 4, fill: getMetricColor(metric, availableMetrics, getMetricInfo, mode) }
            });
        });

        return {
            uniqueUnits,
            scaleGroups,
            leftAxes,
            rightAxes,
            availableMetrics,
            yAxesConfig,
            chartElementsConfig
        };
    } catch (error) {
        console.error("Error in chart rendering configuration:", error);
        return getEmptyConfig();
    }
}

// Helper function to return empty config with proper types
function getEmptyConfig() {
    return {
        uniqueUnits: [] as string[],
        scaleGroups: new Map<string, string[]>(),
        leftAxes: [] as string[],
        rightAxes: [] as string[],
        availableMetrics: [] as string[],
        yAxesConfig: [] as YAxisProps[],
        chartElementsConfig: {
            lines: [] as LineProps[],
            scatters: [] as ScatterProps[]
        }
    };
}

function countDataPointsForMetric(data: any, metric: string): number {
    if (!data?.datasets) return 0;
    const dataset = data.datasets.find((ds: any) => ds.id === metric);
    return dataset?.data?.length || 0;
}